import React, { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { supabase, supabaseConfigError } from '../../config/supabase'

const { FiEye, FiEyeOff, FiUser, FiMail, FiLock } = FiIcons

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [configError, setConfigError] = useState('')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'student'
  })

  const { signIn, signUp, user, userProfile, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (supabaseConfigError) {
      setConfigError('Authentication service is not configured. Please contact support.')
      setError('Authentication service is not configured. Please contact support.')
    }
  }, [])

  useEffect(() => {
    if (authLoading) {
      setLoading(true)
      return
    }

    if (user || userProfile) {
      navigate('/dashboard')
      return
    }

    setLoading(false)
  }, [authLoading, navigate, user, userProfile])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (supabaseConfigError) {
      setError('Authentication service is not configured. Please contact support.')
      return
    }
    setLoading(true)
    setError('')

    try {
      let authResponse

      if (isLogin) {
        authResponse = await signIn(formData.email, formData.password)
      } else {
        authResponse = await signUp(formData.email, formData.password, formData)
      }

      const { data, error: authError } = authResponse || {}

      if (authError) throw authError

      let session = data?.session
      let user = data?.user

      if (!session && !user && supabase) {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) {
          console.error('Error verifying session after authentication attempt:', sessionError)
        }
        session = sessionData?.session
        user = sessionData?.session?.user || user
      }

      if (!session && !user) {
        setError("We couldn’t sign you in—please check your credentials or email verification")
        return
      }

      navigate('/dashboard')
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  if (authLoading && !configError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#00AAFF]/10 to-[#1B4C87]/10 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-12 h-12 rounded-full border-4 border-[#00AAFF]/30 border-t-[#00AAFF] animate-spin" aria-hidden="true" />
          </div>
          <div>
            <p className="text-lg font-semibold text-gray-800">Checking session…</p>
            <p className="text-sm text-gray-500">Making sure you&apos;re signed in.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#00AAFF]/10 to-[#1B4C87]/10 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1B4C87] mb-2" style={{ fontFamily: 'Dancing Script' }}>
            Prosperity
          </h1>
          <p className="text-gray-600">HomeSchool Academy</p>
          <h2 className="text-2xl font-bold text-gray-800 mt-4">
            {isLogin ? 'Welcome Back!' : 'Join Our Academy'}
          </h2>
        </div>

        {configError && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg mb-4">
            <p className="font-semibold">Authentication service unavailable</p>
            <p className="text-sm">
              We can&apos;t connect to the authentication service right now. Please try again later or contact support.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                      placeholder="First name"
                      required={!isLogin}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                      placeholder="Last name"
                      required={!isLogin}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                >
                  <option value="student">Student</option>
                  <option value="parent">Parent</option>
                  <option value="teacher">Teacher</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || Boolean(configError)}
            className="w-full bg-[#00AAFF] text-white py-3 rounded-lg font-medium hover:bg-[#0099E5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
          </button>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#00AAFF] hover:underline ml-1 font-medium"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AuthForm
